import { type FC, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Button,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import type { AssistantDialogMessage } from "./types";
import { splitDialog } from "./model/dialogWindow";
import { threadTexts } from "./texts";
import { MessageRow } from "./MessageRow";

interface Props {
  messages: AssistantDialogMessage[];
  formatTime: (iso: string) => string;
  visibleCount?: number;
  /** Имя зрителя для подписи и букв аватара; без него «Зритель». */
  viewerName?: string | null;
  /**
   * Раскрыт ли блок при открытии. Когда переписка с командой уже идёт,
   * диалог с помощником свёрнут: он нужен как предыстория.
   */
  defaultExpanded?: boolean;
}

/**
 * Переписка зрителя с помощником до обращения (спека 3.1): свёрнутый блок
 * с числом реплик, внутри последние реплики, ранние под «Показать весь
 * диалог».
 */
export const AssistantDialogBlock: FC<Props> = ({
  messages,
  formatTime,
  visibleCount = 6,
  viewerName,
  defaultExpanded = true,
}) => {
  const [showAll, setShowAll] = useState(false);
  const { shown, hidden } = splitDialog(messages, visibleCount);
  const list = showAll ? messages : shown;

  return (
    <Accordion
      defaultExpanded={defaultExpanded && messages.length > 0}
      disableGutters
      sx={{ "&::before": { display: "none" }, borderRadius: 1 }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ px: 2, minHeight: 72 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: "secondary.main" }}>
            <SmartToyOutlinedIcon />
          </Avatar>
          <Typography variant="subtitle1" fontWeight={600}>
            {threadTexts.assistantTitle()}
          </Typography>
          <Chip size="small" label={messages.length} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 2, pt: 0.5 }}>
        <Stack spacing={2.5}>
          {!messages.length && (
            <Typography variant="body2" color="text.secondary">
              {threadTexts.noDialog()}
            </Typography>
          )}
          {hidden > 0 && !showAll && (
            <Button
              size="small"
              onClick={() => setShowAll(true)}
              sx={{ alignSelf: "center" }}
            >
              {threadTexts.showAll(messages.length)}
            </Button>
          )}
          {list.map((m) => {
            const fromViewer = m.role === "USER";
            return (
              <MessageRow
                key={m.id}
                tone={fromViewer ? "viewer" : "assistant"}
                name={
                  fromViewer
                    ? (viewerName ?? threadTexts.viewer())
                    : threadTexts.assistant()
                }
                avatarName={fromViewer ? viewerName : null}
                time={formatTime(m.createdAt)}
                dataAttributes={{ "data-role": m.role }}
              >
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {m.text}
                </Typography>
              </MessageRow>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
